import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Alert,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import FilterDramaIcon from '@mui/icons-material/FilterDrama';
import { Arena, TimeSlot } from '../types/courts';
import { API_ENDPOINTS } from '../api/config';
import axiosInstance from '../api/axios';

interface WeatherData {
  temp: number;
  weathercode: number;
  description: string;
  forecast: {
    time: string;
    weathercode: number;
  }[];
}

interface CourtRecommendation {
  type: 'success' | 'warning' | 'error';
  message: string;
}

const Courts: React.FC = () => {
  const [arenas, setArenas] = useState<Arena[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [selectedArenaId, setSelectedArenaId] = useState<number>(5);
  const [isCoverDialogOpen, setIsCoverDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [areCourtsClosed, setAreCourtsClosed] = useState(false);

  const getWeatherIcon = (code: number) => {
    if (code === 0) return <WbSunnyIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code === 1 || code === 2) return <CloudQueueIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code === 3) return <CloudIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if ((code >= 51 && code <= 65) || code === 95) return <UmbrellaIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code >= 71 && code <= 75) return <AcUnitIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code === 45 || code === 48) return <FilterDramaIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    return <WbSunnyIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
  };

  const getWeatherDescription = (code: number): string => {
    const descriptions: { [key: number]: string } = {
      0: 'Ясно',
      1: 'Преимущественно ясно',
      2: 'Переменная облачность',
      3: 'Пасмурно',
      45: 'Туман',
      48: 'Туман с инеем',
      51: 'Легкая морось',
      53: 'Умеренная морось',
      55: 'Сильная морось',
      61: 'Небольшой дождь',
      63: 'Умеренный дождь',
      65: 'Сильный дождь',
      71: 'Небольшой снег',
      73: 'Умеренный снег',
      75: 'Сильный снег',
      95: 'Гроза',
    };
    return descriptions[code] || 'Ясно';
  };

  const isRainyWeather = (code: number) => {
    return (code >= 51 && code <= 65) || code === 95;
  };

  const isCloudyWeather = (code: number) => {
    return code === 2 || code === 3;
  };

  const getCourtRecommendation = (forecast: WeatherData['forecast']): CourtRecommendation => {
    // Проверяем, есть ли дождь в ближайшие 3 часа
    if (forecast.some(hour => isRainyWeather(hour.weathercode))) {
      return {
        type: 'error',
        message: 'Внимание! В ближайшие 3 часа ожидается дождь. Рекомендуется перекрыть грунтовые корты.'
      };
    }
    
    // Проверяем, есть ли облачность
    if (forecast.some(hour => isCloudyWeather(hour.weathercode))) {
      return {
        type: 'warning',
        message: 'Погода переменчивая. Будьте внимательны к состоянию грунтовых кортов.'
      };
    }
    
    return {
      type: 'success',
      message: 'В ближайшие 3 часа ожидается прекрасная погода для игры в теннис!'
    };
  };

  const checkCourtsStatus = (arena: Arena) => {
    if (!arena || !arena.courts.length) return false;
    
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinutes = currentTime.getMinutes();
    
    // Проверяем все слоты начиная с текущего времени
    return arena.courts.every(court => {
      const courtSlots = getCourtTimeSlots(court.id, arena);
      return courtSlots.every(slot => {
        const slotTime = slot.time.split(':');
        const slotHour = parseInt(slotTime[0]);
        const slotMinutes = parseInt(slotTime[1]);
        
        // Если слот в будущем
        if (slotHour > currentHour || (slotHour === currentHour && slotMinutes >= currentMinutes)) {
          return !isSlotAvailable(court.id, slot.id, arena);
        }
        return true; // Прошлые слоты не учитываем
      });
    });
  };

  const fetchCourtsData = async () => {
    try {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      const response = await axiosInstance.get(API_ENDPOINTS.getArena(formattedDate));
      const clayArena = response.data.find((arena: Arena) => arena.id === 5);
      
      if (clayArena) {
        setArenas([clayArena]);
        setAreCourtsClosed(checkCourtsStatus(clayArena));
      } else {
        setArenas([{
          id: 5,
          title: 'Грунтовые корты',
          courts: [],
          seats: [],
          unAvailableSeats: {},
          cell_color: '#1976d2'
        }]);
        setAreCourtsClosed(false);
      }
    } catch (error) {
      console.error('Error fetching courts data:', error);
      setArenas([{
        id: 5,
        title: 'Грунтовые корты',
        courts: [],
        seats: [],
        unAvailableSeats: {},
        cell_color: '#1976d2'
      }]);
      setAreCourtsClosed(false);
    }
  };

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await axiosInstance.get(
          `https://api.open-meteo.com/v1/forecast?latitude=55.569918&longitude=37.410226&current=temperature_2m,weathercode&hourly=weathercode&forecast_hours=4`
        );
        
        const currentHour = new Date().getHours();
        const nextThreeHours = response.data.hourly.weathercode
          .slice(currentHour, currentHour + 4)
          .map((code: number, index: number) => ({
            time: new Date(response.data.hourly.time[currentHour + index]).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
            weathercode: code
          }));

        setWeather({
          temp: Math.round(response.data.current.temperature_2m),
          weathercode: response.data.current.weathercode,
          description: getWeatherDescription(response.data.current.weathercode),
          forecast: nextThreeHours
        });
      } catch (error) {
        console.error('Error fetching weather:', error);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 300000); // Обновляем каждые 5 минут
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchCourtsData();
  }, [selectedDate]);

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getPrice = (price: number, priceWeekend: number) => {
    return isWeekend(selectedDate) ? priceWeekend : price;
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const isSlotAvailable = (courtId: number, slotId: number, arena: Arena) => {
    return !arena.unAvailableSeats[courtId]?.includes(slotId);
  };

  const getCourtTimeSlots = (courtId: number, arena: Arena) => {
    return arena.seats.filter(slot => slot.court_id === courtId);
  };

  const handleCoverCourts = async () => {
    try {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      const endpoint = areCourtsClosed ? API_ENDPOINTS.openClayCourts : API_ENDPOINTS.closeClayCourts;
      
      await axiosInstance.post(endpoint, {
        date: formattedDate
      });
      
      setSnackbarMessage(areCourtsClosed ? 'Корты успешно открыты' : 'Корты успешно перекрыты');
      setSnackbarOpen(true);
      setIsCoverDialogOpen(false);
      
      // Обновляем данные кортов после успешного запроса
      await fetchCourtsData();
    } catch (error) {
      setSnackbarMessage('Произошла ошибка при изменении статуса кортов');
      setSnackbarOpen(true);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '20px',
          height: 'fit-content',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
            Выбор арены
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="arena-select-label">Выберите арену</InputLabel>
            <Select
              labelId="arena-select-label"
              value={selectedArenaId}
              label="Выберите арену"
              onChange={(e) => setSelectedArenaId(e.target.value as number)}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
              }}
            >
              {arenas.map((arena) => (
                <MenuItem key={arena.id} value={arena.id}>
                  {arena.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '20px',
          height: 'fit-content',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
            Выбор даты
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
            <DatePicker
              value={selectedDate}
              onChange={(newDate) => newDate && setSelectedDate(newDate)}
              format="dd.MM.yyyy"
              minDate={new Date()}
              maxDate={new Date(new Date().setDate(new Date().getDate() + 1))}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                }
              }}
            />
          </LocalizationProvider>
        </div>

        {weather && (
          <>
            <div style={{
              backgroundColor: '#f5f5f5',
              borderRadius: '12px',
              padding: '20px',
              height: 'fit-content',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
                Прогноз погоды
              </Typography>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  padding: '0 0 16px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }}>
                    {getWeatherIcon(weather.weathercode)}
                  </div>
                  <div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '4px' }}>
                      {weather.temp}°C
                    </div>
                    <div style={{ color: '#666', fontSize: '16px' }}>{weather.description}</div>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                  gap: '12px',
                  justifyContent: 'space-between'
                }}>
                  {weather.forecast.map((hour, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer',
                        transform: 'translateY(0)',
                        '&:hover': {
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#666', 
                        fontWeight: '500'
                      }}>
                        {hour.time}
                      </div>
                      {getWeatherIcon(hour.weathercode)}
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        textAlign: 'center' 
                      }}>
                        {getWeatherDescription(hour.weathercode)}
                      </div>
                    </Box>
                  ))}
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: '#f5f5f5',
              borderRadius: '12px',
              padding: '20px',
              height: 'fit-content',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
                Рекомендации
              </Typography>
              <Alert 
                severity={getCourtRecommendation(weather.forecast).type}
                style={{
                  fontSize: '14px',
                  borderRadius: '8px'
                }}
                icon={false}
              >
                {getCourtRecommendation(weather.forecast).message}
              </Alert>
            </div>
          </>
        )}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <Typography variant="h4">Расписание кортов</Typography>
        {selectedArenaId === 5 && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsCoverDialogOpen(true)}
            sx={{
              backgroundColor: areCourtsClosed ? '#4caf50' : '#1976d2',
              '&:hover': {
                backgroundColor: areCourtsClosed ? '#43a047' : '#1565c0',
              },
            }}
          >
            {areCourtsClosed ? 'Открыть корты' : 'Перекрыть корты'}
          </Button>
        )}
      </div>

      <div style={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: '-ms-autohiding-scrollbar',
        position: 'relative',
        backgroundColor: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px'
      }}>
        <div style={{
          display: 'flex',
          gap: '20px',
          minWidth: 'min-content',
          paddingBottom: '12px'
        }}>
          {arenas
            .filter(arena => !selectedArenaId || arena.id === selectedArenaId)
            .flatMap(arena => 
              arena.courts.map((court) => (
                <div
                  key={`${arena.id}-${court.id}`}
                  style={{
                    width: '280px',
                    flex: '0 0 280px',
                    backgroundColor: court.cell_color,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{
                    padding: '16px',
                    color: '#fff',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {court.title}
                  </div>
                  <div style={{
                    backgroundColor: '#fff',
                    padding: '12px',
                    flex: 1
                  }}>
                    {getCourtTimeSlots(court.id, arena).map((slot: TimeSlot) => (
                      <div
                        key={slot.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '60px 1fr auto',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px',
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: isSlotAvailable(court.id, slot.id, arena) ? 'transparent' : '#fafafa',
                          transition: 'background-color 0.2s ease',
                          borderRadius: '4px',
                          margin: '4px 0'
                        }}
                      >
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {formatTime(slot.time)}
                        </div>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#1976d2',
                          textAlign: 'right'
                        }}>
                          {getPrice(slot.price, slot.price_weekend)}
                          <span style={{ fontSize: '13px', marginLeft: '2px', color: '#666' }}>р/ч</span>
                        </div>
                        <Chip
                          label={isSlotAvailable(court.id, slot.id, arena) ? 'Свободно' : 'Занято'}
                          color={isSlotAvailable(court.id, slot.id, arena) ? 'success' : 'error'}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '12px',
                            minWidth: '80px',
                            '& .MuiChip-label': {
                              padding: '0 8px'
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
        </div>
      </div>

      <Dialog
        open={isCoverDialogOpen}
        onClose={() => setIsCoverDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '20px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          {areCourtsClosed ? 'Открытие кортов' : 'Перекрытие кортов'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {areCourtsClosed 
              ? 'Вы уверены, что хотите открыть все корты на арене "Грунт"?'
              : 'Вы уверены, что хотите перекрыть все корты на арене "Грунт"? Это действие сделает все корты недоступными для бронирования.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsCoverDialogOpen(false)}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleCoverCourts}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            top: '24px'
          }
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ 
            width: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Courts;